import React, { useEffect } from "react";
import AppContainer from "@src/common/app_container";
import { useBusinessProfileAPI } from "@src/api/business_profile_api";
import { BusinessProfile, BusinessProfileStatus } from "@src/model/business_profile";
import { useNavigate } from "react-router-dom";
import Alert, { AlertProps } from "@src/common/alert";
import DataTable from "react-data-table-component";


export default function BusinessProfileIndex() {
  const [data, setData] = React.useState<BusinessProfile[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [status, setStatus] = React.useState<AlertProps | null>(null);
  const businessProfileAPI = useBusinessProfileAPI();
  const navigate = useNavigate();

  const columns = [
    {
      name: "Name",
      selector: (row: BusinessProfile) => row.name || "",
      sortable: true,
      width: "250px"
    },
    {
      name: "Status",
      cell: (row: BusinessProfile) => (
        <span className={`px-2 py-1 rounded text-white ${row.status === BusinessProfileStatus.ACTIVE ? "bg-green-500" : row.status === BusinessProfileStatus.FAILED ? "bg-red-500" : "bg-yellow-500"}`}>
          {row.status}
        </span>
      ),
      sortable: true,
      width: "150px"
    },
    {
      name: "Campaigns",
      selector: (row: any) => row.campaigns.length,
      sortable: true,
      cell: (row: any) => {
        return <a href={`/business-profiles/${row.id}/campaigns`} style={{ textDecoration: "underline" }}>{row.campaigns.length}</a>;
      },
      width: "150px",
    },
    {
      name: "Actions",
      cell: (row: BusinessProfile) => (
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/business-profiles/edit/${row.id}`)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-sm"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded text-sm"
          >
            Delete
          </button>
        </div>
      ),
      width: "300px",
    }
  ];

  const fetchBusinessProfiles = () => {
    setLoading(true);
    businessProfileAPI.getAllBusinessProfiles()
      .then((response) => {
        setData(response);
        setLoading(false);
      })
      .catch((error) => {
        setStatus({
          type: "error",
          message: "Failed to load business profiles: " + error.message
        });
        setLoading(false);
      });
  };

  const handleDelete = (id?: string) => {
    if (!id) return;

    if (window.confirm("Are you sure you want to delete this business profile? This action cannot be undone.")) {
      businessProfileAPI.deleteBusinessProfile(id)
        .then(() => {
          setStatus({
            type: "success",
            message: "Business profile deleted successfully"
          });
          fetchBusinessProfiles();
        })
        .catch((error) => {
          setStatus({
            type: "error",
            message: "Failed to delete business profile: " + error.message
          });
        });
    }
  };

  useEffect(() => {
    fetchBusinessProfiles();
  }, []);

  return (
    <AppContainer>
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center gap-2 mb-6">
          <h1 className="text-2xl font-bold">Business Profiles</h1>
          <button
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex-shrink-0 overflow-clip"
            onClick={() => navigate("/business-profiles/add")}
          >
            <span className="hidden sm:inline">Add Business Profile</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>

        {status && (
          <div className="mb-4">
            <Alert {...status} onClose={() => setStatus(null)} />
          </div>
        )}

        <DataTable
          columns={columns}
          data={data}
          pagination
          progressPending={loading}
          progressComponent={<div className="py-6">Loading business profiles...</div>}
          noDataComponent={<div className="py-6">No business profiles found</div>}
        />
      </div>
    </AppContainer>
  );
}